// Moved by `git mv` from
// `src/hooks/query/integrations/useQueryEHRAppointments.ts`, which survives as a
// delegate — so `git log --follow` on THIS path stops at the move and the
// pre-move history is `git log --follow` on the source path. Its suite stayed at
// the old path, where it drives the delegate end to end and asserts the copy, the
// PHI-safe log, the re-pair branch and the two refetch flags.
//
// TIER T2 (PORTS). The record filed it as "jotai atom-family write, three app
// hooks, branch-and-suppress logic". All four legs are still there and all four
// are app: two atom writes, a three-term gate composed from two app queries and
// an app resolver, and a three-way error branch over shared app predicates.
//
// THE TWO ATOM WRITES ARE PORTS BECAUSE THEIR POSITION IS BEHAVIOUR, not because
// jotai is banned here (it is, but that is not the interesting part).
// `setRePairRequired(false)` sits BETWEEN the await and the return, so it fires
// on every healthy response and is what self-clears the flag; and
// `setRePairRequired(true)` is the FIRST arm of the catch, so it short-circuits
// before either toast branch is consulted. Both ports fire in exactly those
// positions and the suite at the old path already pins both.
//
// `throw new Error()` IS TRANSCRIBED, argument-less. It discards the original
// error rather than re-throwing it, which is not tidy and is load-bearing twice
// over: the query's `error` never carries an axios response, so nothing
// downstream can render a backend string that might hold PHI, and `retry: false`
// means the discarded error is never needed for a retry decision. Re-throwing
// `error` would read better and would put an EHR response body into React
// Query's cache.
//
// THE GATE IS `skipToken`, NOT `enabled`, and the difference is observable:
// `skipToken` leaves the query idle with no `queryFn`, where `enabled: false`
// leaves it pending. The delegate's callers read `isLoading` off this.
import { skipToken, useQuery } from '@tanstack/react-query';

import { ehrQueryKeys } from '../../contracts/queryKeys';
import { getEHRAppointments } from '../api/schedule';

type Params = {
  /**
   * `formatToDateOnly(date) ?? undefined` — the date-only string, resolved
   * app-side, and part of the cache identity in exactly this form.
   */
  date?: string;
  /**
   * The three remaining identity terms, resolved app-side: a signed-in user, a
   * connected EHR profile, and that profile's provider being an OLD-FRAMEWORK
   * integration. Composed from two app queries and an app resolver, none of
   * which a `/data` zone can reach.
   */
  canFetchAppointments?: boolean;
  /**
   * `!isRePairRequired`. While re-pairing is required the focus/reconnect
   * refetch storm is stopped, but mount, date-nav and explicit retry stay live
   * so a successful fetch self-clears the flag — which is why this is NOT part
   * of the `skipToken` gate.
   */
  refetchOnFocusAndReconnect?: boolean;
  /**
   * Fired BETWEEN the await and the return, on every healthy response. The
   * delegate clears the per-provider re-pair-required atom from here.
   */
  onAppointmentsFetched?: () => void;
  /**
   * Fired as the FIRST statement of the catch, before the argument-less throw.
   * The delegate owns the whole three-way branch — re-pair, connection error,
   * generic axios — because two of the three arms only decide words and the
   * third writes an app atom.
   */
  onAppointmentsFetchError?: (error: unknown) => void;
};

/**
 * The clinician's EHR appointments for one day (`GET /ehr/appointments`).
 *
 * OLD-FRAMEWORK INTEGRATIONS ONLY, which is why `canFetchAppointments` is an
 * input rather than something this hook could decide: the capability-driven
 * integrations read their day through `useQueryIntegrationsMixinGetSchedule`
 * instead.
 */
export function useQueryEHRAppointments({
  date,
  canFetchAppointments,
  refetchOnFocusAndReconnect = true,
  onAppointmentsFetched,
  onAppointmentsFetchError,
}: Params = {}) {
  return useQuery({
    queryKey: ehrQueryKeys.ehrAppointments(date),
    retry: false,
    refetchOnWindowFocus: refetchOnFocusAndReconnect,
    refetchOnReconnect: refetchOnFocusAndReconnect,
    queryFn:
      date && canFetchAppointments
        ? async () => {
            try {
              const { data } = await getEHRAppointments(date);
              onAppointmentsFetched?.();
              return data;
            } catch (error) {
              onAppointmentsFetchError?.(error);
              throw new Error();
            }
          }
        : skipToken,
  });
}
